<template>
  <div class="pt-0">
    <NuxtLayout
      name="teasers"
      v-bind="{ loading }"
      :no-results="!teasers.length"
      @refresh="handleRefetch"
    >
      <HighFrequencyGameTeaser
        v-for="(teaser, index) in teasers"
        :key="`${index}_${locale}`"
        v-bind="{
          ...teaser,
          licensedTerritory,
        }"
        @click="handleTeaserClick(teaser)"
      />
    </NuxtLayout>
  </div>
</template>

<script lang="ts" setup>
import type { HighFrequencyTeaserPayload } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { KnownProductType } from '#api/shared/@types/generated/backend/graphql-schema-types'
import HighFrequencyGameTeaser from '#high-frequency-game/app/components/HighFrequencyGameTeaser.vue'

import { useFetchOptions } from '../utils/fetchOptions'

const { locale } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const {
  loading,
  refetch: kenoRefetch,
  result: kenoResult,
} = useProductCatalogQuery(
  { licensedTerritory, productType: KnownProductType.HighFrequencyGame },
  useFetchOptions(),
)

const teasers = computed(() => {
  const result = kenoResult.value?.productCatalog
  if (!isGraphqlSuccess(result)) return []
  return result.elements.filter(
    (teaser) => teaser.__typename !== 'FreeToPlayTeaserPayload',
  ) as HighFrequencyTeaserPayload[]
})

const handleRefetch = () => kenoRefetch()

const TRACKING_LIST_NAME = 'Home Keno Teasers'
const TRACKING_LIST_ID = 'home_keno_teasers'

const { createTeaserListItems } = useTrackingData()
const { pushSelectListItemEvent, pushViewListEvent } = useTracking()
const { logTrackingError } = useTrackingErrorLog()

const trackingTeaserListItems = ref<ReturnType<typeof createTeaserListItems>>([])

const handleTeaserClick = (teaser: HighFrequencyTeaserPayload) => {
  const teaserItem = trackingTeaserListItems.value.find((item) => item.item_id === teaser.id)

  if (!teaserItem) return

  try {
    pushSelectListItemEvent({
      items: [teaserItem],
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })
  } catch (err) {
    logTrackingError('select_list_item', err)
  }
}

let unwatchTeasers: Maybe<() => void> = null
unwatchTeasers = watchImmediate(teasers, (list) => {
  if (!list || list.length === 0) return

  try {
    trackingTeaserListItems.value = createTeaserListItems(list)
    pushViewListEvent({
      items: trackingTeaserListItems.value,
      list_id: TRACKING_LIST_ID,
      list_name: TRACKING_LIST_NAME,
    })

    if (unwatchTeasers) unwatchTeasers()
  } catch (err) {
    logTrackingError('view_list', err)
  }
})
</script>
